import React, { useMemo, useState, useRef } from 'react';
import DashboardLayout from '../../../layouts/DashboardLayout';
import PageHeader from '../../../components/PageHeader';
import { useTranslation } from 'react-i18next';
import useAuthStore from '../../../store/auth';
import { Navigate } from 'react-router-dom';
import { DOCS_SECTIONS, DOCS_CONTENT } from '../../../docs/docsRegistry';
import MarkdownViewer from '../../../components/docs/MarkdownViewer';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';

function DocsV1Index() {
  const { t } = useTranslation();
  const { roles, permissions } = useAuthStore();

  const [selectedDocId, setSelectedDocId] = useState('overview');
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const pdfContainerRef = useRef(null);

  const selectedContent = useMemo(() => {
    return DOCS_CONTENT[selectedDocId] || null;
  }, [selectedDocId]);

  const hasAccess =
    roles?.includes('merchant') ||
    roles?.includes('admin') ||
    roles?.includes('super-admin') ||
    permissions?.includes('view_docs');

  const handleDownloadPdf = async () => {
    if (!pdfContainerRef.current) return;
    setIsGeneratingPdf(true);
    try {
      const html2pdfModule = await import('html2pdf.js');
      const html2pdf = html2pdfModule.default || html2pdfModule;
      const element = pdfContainerRef.current;

      const opt = {
        margin: [10, 10, 10, 10],
        filename: 'PAYD-API-Documentation.pdf',
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, logging: false },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
      };

      await html2pdf().set(opt).from(element).save();
      toast.success(t('api_docs.pdf_download_success', 'Documentation PDF downloaded successfully!'));
    } catch (err) {
      console.error('Failed to generate PDF:', err);
      toast.error(t('api_docs.pdf_download_error', 'Failed to generate PDF. Please try again.'));
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  if (!hasAccess) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <DashboardLayout>
      <PageHeader title={t('api_docs.page_title')} description={t('api_docs.page_description')}>
        <button
          onClick={handleDownloadPdf}
          disabled={isGeneratingPdf}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-cyan-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-cyan-500 active:bg-cyan-700 disabled:opacity-50 transition-all duration-150 cursor-pointer"
        >
          {isGeneratingPdf ? (
            <>
              <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              <span>{t('api_docs.downloading_pdf', 'Generating PDF...')}</span>
            </>
          ) : (
            <>
              <ArrowDownTrayIcon className="w-5 h-5" aria-hidden="true" />
              <span>{t('api_docs.download_pdf', 'Download PDF')}</span>
            </>
          )}
        </button>
      </PageHeader>

      <div className="mt-4 flex flex-col lg:flex-row w-full max-w-6xl gap-6">
        {/* Grouped Sidebar Navigation */}
        <div className="w-full shrink-0 lg:w-64">
          <div className="bg-white rounded-lg shadow p-4 space-y-6 sticky top-4 border border-gray-100">
            {DOCS_SECTIONS.map((section, idx) => (
              <div key={idx}>
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-2">
                  {section.title}
                </h3>
                <ul className="space-y-1">
                  {section.items.map((item) => (
                    <li key={item.id}>
                      <button
                        onClick={() => setSelectedDocId(item.id)}
                        className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 ${
                          selectedDocId === item.id
                            ? 'bg-cyan-50 text-cyan-700 font-semibold'
                            : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                        }`}
                      >
                        {item.name}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Markdown Content Panel */}
        <div className="flex-1 w-full min-w-0">
          {selectedContent ? (
            <div className="bg-white rounded-lg shadow p-6 lg:p-8 border border-gray-100">
              <MarkdownViewer content={selectedContent} onSelectDoc={setSelectedDocId} />
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow p-6 text-gray-500">
              {t('api_docs.select_topic')}
            </div>
          )}
        </div>
      </div>

      {/* Hidden Printable PDF Container containing ALL documentation sections */}
      <div className="hidden">
        <div ref={pdfContainerRef} className="p-8 bg-white text-gray-900 font-sans max-w-4xl">
          {/* Header Banner */}
          <div className="border-b-2 border-cyan-600 pb-6 mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">PAYD API Documentation</h1>
            <p className="text-sm text-gray-600">
              Environment Base URL: <code className="text-cyan-700 bg-cyan-50 px-1.5 py-0.5 rounded font-mono">{process.env.REACT_APP_PAYD_API_BASE_URL || 'http://api.payd.local'}</code>
            </p>
            <p className="text-xs text-gray-400 mt-2">Exported on: {new Date().toLocaleDateString()}</p>
          </div>

          {/* Table of Contents */}
          <div className="mb-10 bg-gray-50 p-6 rounded-lg border border-gray-200">
            <h2 className="text-lg font-bold text-gray-900 mb-4 uppercase tracking-wider">Table of Contents</h2>
            <ul className="space-y-2 text-sm text-cyan-800">
              {DOCS_SECTIONS.map((sec, sIdx) => (
                <li key={sIdx} className="font-semibold text-gray-800">
                  {sec.title}
                  <ul className="pl-4 mt-1 space-y-1 font-normal text-cyan-700">
                    {sec.items.map((it, iIdx) => (
                      <li key={iIdx}>• {it.name}</li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          </div>

          {/* All Documentation Sections */}
          {DOCS_SECTIONS.map((section, sIdx) => (
            <div key={sIdx} className="mb-12">
              <div className="bg-cyan-50 border-l-4 border-cyan-600 px-4 py-2 mb-6">
                <h2 className="text-xl font-bold text-cyan-900 uppercase tracking-wide">{section.title}</h2>
              </div>
              {section.items.map((item, iIdx) => (
                <div key={iIdx} className="mb-10 page-break-inside-avoid">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">{item.name}</h3>
                  {DOCS_CONTENT[item.id] && (
                    <MarkdownViewer content={DOCS_CONTENT[item.id]} />
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
}

export default DocsV1Index;
